// src/components/StepMenuNoticias.jsx
import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';

const StepMenuNoticias = ({ handleScrollTo, pasoActivo, news, formatDate }) => {
  const [segmentPositions, setSegmentPositions] = useState([]);
  const listRef = useRef(null);
  const itemRefs = useRef([]);

  // Inicializar refs para cada elemento
  useEffect(() => {
    itemRefs.current = news.map(() => React.createRef());
  }, [news]);

  // Función para calcular posiciones mejorada
  const calculatePositions = () => {
    if (listRef.current && news.length > 0 && itemRefs.current.length > 0) {
      const positions = [];
      let cumulativeTop = 0;

      itemRefs.current.forEach((ref, index) => {
        let height = 60; // altura por defecto
        let isVisible = false;

        if (ref?.current) {
          // Intentar obtener la altura real
          const rect = ref.current.getBoundingClientRect();
          if (rect.height > 0) {
            height = rect.height;
            isVisible = true;
          } else {
            // Si no es visible, usar altura estimada basada en contenido
            const primaryText = news[index].summary || '';
            const secondaryText = formatDate(news[index].publish_date) || '';
            
            // Estimar altura basada en la longitud del texto
            const primaryLines = Math.ceil(primaryText.length / 40); // ~40 caracteres por línea
            const estimatedHeight = Math.max(
              60, // altura mínima
              (primaryLines * 18) + 30 + (secondaryText ? 15 : 0) + 24 // padding
            );
            height = estimatedHeight;
          }
        }

        positions.push({
          top: cumulativeTop,
          height: height,
          isActive: news[index].new_id === pasoActivo,
          isVisible: isVisible
        });

        cumulativeTop += height;
      });

      setSegmentPositions(positions);
    }
  };

  // Calcular posiciones inicial y cuando cambien las props
  useEffect(() => {
    // Calcular inmediatamente con estimaciones
    calculatePositions();
    
    // Y también después de un timeout para elementos renderizados
    const timer = setTimeout(() => {
      calculatePositions();
    }, 100);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [news, pasoActivo]);

  // Recalcular en scroll - con throttling para mejor performance
  useEffect(() => {
    let throttleTimer = null;
    
    const throttledCalculate = () => {
      if (throttleTimer) return;
      
      throttleTimer = setTimeout(() => {
        calculatePositions();
        throttleTimer = null;
      }, 16); // ~60fps
    };

    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', throttledCalculate);
      return () => {
        listElement.removeEventListener('scroll', throttledCalculate);
        if (throttleTimer) clearTimeout(throttleTimer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [news, pasoActivo]);

  // AGREGAR: Observer para detectar cuando los elementos entran en viewport
  useEffect(() => {
    if (!listRef.current || itemRefs.current.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let shouldRecalculate = false;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            shouldRecalculate = true;
          }
        });
        
        if (shouldRecalculate) {
          setTimeout(calculatePositions, 10);
        }
      },
      {
        root: listRef.current,
        rootMargin: '50px' // Detectar elementos un poco antes de que sean visibles
      }
    );

    itemRefs.current.forEach((ref) => {
      if (ref?.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [news]);

  return (
    <Box
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Encabezado Noticias - FIJO */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "left",
          gap: "8px",
          backgroundColor: "#E7F0FF",
          padding: "6px 12px",
          borderRadius: "20px",
          marginBottom: "12px",
          width: "100%",
          flexShrink: 0
        }}
      >
        <NotificationsOutlinedIcon
          sx={{ 
            fontSize: 20, 
            color: "#0F52BD" 
          }}
        />
        <Typography sx={{ color: "#0F52BD", fontSize: '14px' }}>
          Noticias
        </Typography>
      </Box>

      {/* Contenedor con scroll para la lista de noticias */}
      <Box 
        sx={{ 
          display: "flex", 
          ml: 3,
          flex: 1,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Contenedor scrolleable */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            maxHeight: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '3px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '10px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#c1c1c1 transparent'
          }}
        >
          {/* Lista de noticias */}
          <List 
            ref={listRef}
            sx={{ 
              flex: 1,
              padding: 0,
              margin: 0,
              pl: '20px',
              position: 'relative'
            }}
          >
            {/* Segmentos de línea vertical dinámicos */}
            {segmentPositions.map((segment, index) => (
              <Box
                key={`segment-${index}`}
                sx={{
                  position: 'absolute',
                  left: '8px',
                  top: `${segment.top}px`,
                  width: '3px',
                  height: `${segment.height}px`,
                  backgroundColor: segment.isActive ? '#1a73e8' : '#ddd',
                  borderRadius: '1px',
                  zIndex: 1,
                  transition: 'background-color 0.2s ease-in-out',
                  // AGREGAR indicador visual para elementos no visibles (opcional)
                  opacity: segment.isVisible ? 1 : 0.8
                }}
              />
            ))}

            {news.map((item, index) => (
              <ListItemButton
                key={item.new_id}
                ref={itemRefs.current[index]}
                onClick={() => {
                  handleScrollTo(index);
                }}
                selected={item.new_id === pasoActivo}
                sx={{
                  px: 0,
                  py: 1.5,
                  justifyContent: "flex-start",
                  textAlign: "left",
                  color: item.new_id === pasoActivo ? "#1a73e8" : "#555",
                  fontWeight: item.new_id === pasoActivo ? "bold" : "normal",
                  backgroundColor: "transparent",
                  minHeight: 'auto',
                  position: 'relative',
                  zIndex: 2,
                  '&.Mui-selected': {
                    backgroundColor: "transparent",
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: "#f5f5f5",
                  },
                  '&:hover': {
                    backgroundColor: "#f5f5f5",
                  }
                }}
              >
                <ListItemText
                  primary={item.title}
                  secondary={formatDate(item.publish_date)}
                  primaryTypographyProps={{
                    fontSize: 13,
                    lineHeight: 1.3,
                    sx: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }
                  }}
                  secondaryTypographyProps={{
                    fontSize: 10,
                    color: "#999",
                    lineHeight: 1.2
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );
};

export default StepMenuNoticias;